import React, { ReactNode } from "react";
import { ContentPagePanelType, FrontPagePanelSize } from "ui/Scripts/source/models/enums";
import { TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { IPagePanel, PagePanel } from "../common/pagePanel";
import { IReportParameterValue, ReportParameterValue } from "../report/reportParameterValue";
import { ISavedReport, SavedReport } from "../report/savedReport";
import { IChartItem } from "./chartItem";
import { AppChartIcon } from "source/components/framework/Icon/AppChartIcon";
import { ISharedPanel } from "../panel/sharedPanel";

export interface ISavedChartPanel extends ISavedReport, IPagePanel {
    chartId: string;
    reportTitle: string;
    disabled: boolean; //True if saved chart is wrong locale compared to ui locale

    isNew(): boolean;
    getReportParameterValues(chartItem: IChartItem): IReportParameterValue[];
}

export class SavedChartPanel extends SavedReport implements ISavedChartPanel {
    chartId: string;
    reportTitle: string;
    disabled: boolean;
    panelType: number;
    size: number;
    position: number;
    visible: boolean;

    constructor();
    constructor(obj: ISavedChartPanel);
    constructor(obj?: any) {
        super(obj);
        this.chartId = obj && obj.chartId || "";
        this.reportTitle = obj && obj.reportTitle || "";
        this.disabled = obj && obj.disabled || false;
        //IPagePanel
        this.panelType = obj && obj.panelType || 0;
        this.size = obj && obj.size || 0;
        this.position = obj && obj.position || 0;
        this.visible = obj && obj.visible || false;
    }

    getIcon(props?: any): ReactNode {
        console.log("SavedChartPanel.getIcon called", props);
        return React.createElement(AppChartIcon, props);
    }

    getTitle(): string {
        return this.reportTitle;
    }

    getId(): string {
        return this.id;
    }

    getDisabled(): boolean {
        return this.disabled;
    }

    getUniqueId(): string {
        return this.reportId + "#" + this.chartId + "#" + this.id;
    }

    getHash(): string {
        return super.getHash() + "#" + this.chartId + "#" + this.reportTitle + "#" + this.size.toString(10) + "#" + this.position.toString(10) + "#" + this.visible.toString();
    }

    isNew(): boolean {
        return !this.id;
    }

    getReportParameterValues(chartItem: IChartItem): IReportParameterValue[] {
        const result: IReportParameterValue[] = [];
        if (!this.reportFilter) return;
        for (const col of chartItem.taskDataRow.cols) {
            const typeCol = chartItem.taskType.formCols.find(i => i.field === col.field);
            if (!typeCol || !Object.prototype.hasOwnProperty.call(this.reportFilter, col.field)) continue;
            result.push(ReportParameterValue.createReportParameterValue(col.field, this.reportFilter[col.field]));
        }
        return result;
    }

    static GetReportFilter(chartItem: IChartItem): any {
        const result = {};
        for (const col of chartItem.taskDataRow.cols) {
            const typeCol = chartItem.taskType.formCols.find(i => i.field === col.field);
            if (!typeCol) continue;
            const defaultCol = TaskDataRowCol.getDefaultValueTaskDataRowCol(typeCol);
            result[col.field] = TaskDataRowCol.getTaskDataRowColDbValue(typeCol, defaultCol.valueStr, defaultCol.valueNbr);
        }
        return result;
    }

    static CreateBasicSavedChartPanel(id: string, reportId: string, reportTitle: string, chartId: string, position: number, reportFilter: any, applicationPermissionType: number): ISavedChartPanel {
        const result = new SavedChartPanel();
        result.id = id;
        result.reportId = reportId;
        result.reportTitle = reportTitle;
        result.chartId = chartId;
        result.panelType = ContentPagePanelType.Chart;
        result.size = FrontPagePanelSize.OneXOne;
        result.position = position;
        result.visible = false;
        result.reportFilter = reportFilter;
        result.applicationPermissionType = applicationPermissionType;
        result.generalFilter = {};
        result.sortColumn = "";
        result.sortOrderIsAsc = true;
        return result;
    }

    static CreateSavedChartPanel(chartItem: IChartItem, position: number, applicationPermissionType: number): ISavedChartPanel {
        return SavedChartPanel.CreateBasicSavedChartPanel(chartItem.id, chartItem.reportName, chartItem.reportDescription, chartItem.chartNames[0], position, SavedChartPanel.GetReportFilter(chartItem), applicationPermissionType);
    }

    static CreateSavedChartPanelBySharedPanel(sharedPanel: ISharedPanel, position: number, applicationPermissionType: number): ISavedChartPanel {
        const result = SavedChartPanel.CreateBasicSavedChartPanel(sharedPanel.id, sharedPanel.reportId, sharedPanel.id, sharedPanel.chartId, position, { ...sharedPanel.reportFilter }, applicationPermissionType);
        result.generalFilter = { ...sharedPanel.generalFilter };
        result.sortColumn = sharedPanel.sortColumn;
        result.sortOrderIsAsc = sharedPanel.sortOrderIsAsc;
        return result;
    }

    static getDashBoardPanels(panels: IPagePanel[], savedChartPanels: ISavedChartPanel[], reportChartItems: IChartItem[], applicationPermissionType: number): IPagePanel[] {
        const result = panels ?? [];
        const chartPanels = savedChartPanels ?? [];
        const newChartPanels: ISavedChartPanel[] = [];
        //Set chart panel titles and add missing chart panels
        for (const reportChartItem of reportChartItems) {
            let chartPanel = chartPanels.find(i => i.reportId === reportChartItem.reportName);
            if (chartPanel) {
                chartPanel.reportTitle = reportChartItem.reportDescription;
            } else {
                chartPanel = SavedChartPanel.CreateSavedChartPanel(reportChartItem, result.length, applicationPermissionType);
            }
            newChartPanels.push(chartPanel);
        }
        return PagePanel.sortPagePanels(result.concat(newChartPanels));
    };
}